import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Avatar } from "@/components/community/ChatList";
import { Stars } from "@/components/social/Stars";
import { StarBadge } from "@/components/social/Stars";
import { coverStyle, COVER_PRESETS } from "@/constants/covers";
import {
  Camera, UserPlus, UserCheck, UserMinus, Check, X, MessageCircle, Rss, Loader2, Pencil, Image as ImageIcon,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);

const Count = ({ value, label, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className="text-white font-semibold text-sm">{value}</div>
    <div className="text-[11px] text-white/45">{label}</div>
  </div>
);

/** Portada, foto, biografía, nivel y acciones (amistad, seguir, mensaje). */
export const ProfileHeader = ({ data, onReload }) => {
  const { profile, counts, level, relation } = data;
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [covers, setCovers] = useState(false);
  const [name, setName] = useState(profile.name || "");
  const [bio, setBio] = useState(profile.bio || "");
  const coverRef = useRef(null);
  const photoRef = useRef(null);
  const navigate = useNavigate();

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onReload?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const upload = async (file, field) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await call(async () => {
      const { data: m } = await userApi.post("/community/media", fd);
      await userApi.patch("/community/profile", { [field]: m.url });
    }, field === "cover_url" ? "Portada actualizada" : "Foto actualizada");
  };

  const pickPreset = (id) => call(
    () => userApi.patch("/community/profile", { cover_url: `preset:${id}` }),
    "Portada actualizada");

  const saveInfo = () => call(
    () => userApi.patch("/community/profile", { name: name.trim(), bio: bio.trim() }),
    "Perfil actualizado").then(() => setEditing(false));

  const message = () => call(async () => {
    await userApi.post("/community/conversations", { type: "dm", member: profile.key });
    navigate("/comunidad");
  });

  return (
    <div className="glass-card overflow-hidden" data-testid="profile-header">
      <div className="relative h-40 sm:h-52"
        style={coverStyle(profile.cover, profile.key, mediaUrl)}>
        <div className="absolute inset-0" style={{ background: "rgba(5,2,3,0.35)" }} />
        {relation.is_me && (
          <>
            <button data-testid="profile-cover-btn" onClick={() => setCovers((v) => !v)}
              className="absolute right-3 top-3 glass-btn px-3 py-1.5 rounded-xl text-[11px] flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" /> Portada
            </button>
            <input ref={coverRef} type="file" accept="image/*" className="hidden"
              data-testid="profile-cover-file"
              onChange={(e) => upload(e.target.files?.[0], "cover_url")} />
            {covers && (
              <div data-testid="cover-picker"
                className="absolute right-3 top-14 w-64 p-3 rounded-2xl border border-white/10 z-20"
                style={{ background: "rgba(10,10,14,0.92)", backdropFilter: "blur(18px)" }}>
                <div className="text-[11px] text-white/55 mb-2">Portadas prediseñadas</div>
                <div className="grid grid-cols-3 gap-2">
                  {COVER_PRESETS.map((p) => (
                    <button key={p.id} data-testid={`cover-preset-${p.id}`} title={p.name}
                      onClick={() => { setCovers(false); pickPreset(p.id); }}
                      className="h-12 rounded-lg border border-white/10 hover:border-red-400/50 transition-colors"
                      style={{ background: p.background }} />
                  ))}
                </div>
                <button data-testid="cover-upload-btn" onClick={() => { setCovers(false); coverRef.current?.click(); }}
                  className="glass-btn w-full mt-3 px-3 py-2 rounded-xl text-[11px] flex items-center justify-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5" /> Subir mi foto
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-end gap-4 -mt-10">
          <div className="relative">
            <div className="rounded-full p-1" style={{ background: "rgba(5,2,3,0.8)" }}>
              <Avatar person={profile} size={84} />
            </div>
            {relation.is_me && (
              <>
                <button data-testid="profile-photo-btn" onClick={() => photoRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full border border-red-400/50 bg-black/80 flex items-center justify-center text-red-300">
                  <Camera className="w-3.5 h-3.5" />
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden"
                  data-testid="profile-photo-file"
                  onChange={(e) => upload(e.target.files?.[0], "photo_url")} />
              </>
            )}
          </div>
          <div className="flex-1 min-w-0 pb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-white font-heading font-bold text-lg truncate"
                data-testid="profile-name">{profile.name}</h1>
              <StarBadge level={level} />
              {profile.ambassador && (
                <span title="Embajador del mes" data-testid="profile-ambassador-badge"
                  className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300/50 text-amber-200 flex items-center gap-1">
                  <img src="/logo-digital-forge.jpg" alt="" className="w-3 h-3 rounded-full" /> EMBAJADOR
                </span>
              )}
              {profile.is_admin && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white">ADMIN</span>
              )}
            </div>
            <Stars level={level} />
          </div>
        </div>

        {editing ? (
          <div className="mt-4 space-y-2" data-testid="profile-edit">
            <input data-testid="profile-edit-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              className="glass-field w-full px-3 py-2 rounded-xl text-sm text-white outline-none" />
            <textarea data-testid="profile-edit-bio" value={bio} rows={2}
              onChange={(e) => setBio(e.target.value)} placeholder="Cuenta algo de ti"
              className="glass-field w-full px-3 py-2 rounded-xl text-sm text-white outline-none resize-none" />
            <div className="flex gap-2">
              <button data-testid="profile-edit-save" onClick={saveInfo} disabled={busy}
                className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Guardar
              </button>
              <button data-testid="profile-edit-cancel" onClick={() => setEditing(false)}
                className="glass-btn px-4 py-2 rounded-xl text-xs">Cancelar</button>
            </div>
          </div>
        ) : (
          profile.bio && (
            <p className="mt-3 text-sm text-white/75 whitespace-pre-wrap" data-testid="profile-bio">
              {profile.bio}
            </p>
          )
        )}

        <div className="flex items-center gap-6 mt-4">
          <Count value={counts.friends} label="amigos" testid="count-friends" />
          <Count value={counts.followers} label="seguidores" testid="count-followers" />
          <Count value={counts.posts} label="publicaciones" testid="count-posts" />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          {relation.is_me ? (
            <button data-testid="profile-edit-btn" onClick={() => setEditing((v) => !v)}
              className="glass-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Editar perfil
            </button>
          ) : (
            <>
              {relation.is_friend ? (
                <button data-testid="profile-unfriend" disabled={busy}
                  onClick={() => call(() => userApi.delete(`/social/friends/${profile.key}`), "Ya no son amigos")}
                  className="glass-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <UserMinus className="w-3.5 h-3.5" /> Amigos · Eliminar
                </button>
              ) : relation.incoming_id ? (
                <>
                  <button data-testid="profile-accept" disabled={busy}
                    onClick={() => call(() => userApi.post(`/social/friends/${relation.incoming_id}/accept`), "¡Ya son amigos!")}
                    className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5" /> Aceptar solicitud
                  </button>
                  <button data-testid="profile-reject" disabled={busy}
                    onClick={() => call(() => userApi.post(`/social/friends/${relation.incoming_id}/reject`), "Solicitud rechazada")}
                    className="glass-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                    <X className="w-3.5 h-3.5" /> Rechazar
                  </button>
                </>
              ) : relation.outgoing ? (
                <button data-testid="profile-cancel-request" disabled={busy}
                  onClick={() => call(() => userApi.delete(`/social/friends/request/${profile.key}`), "Solicitud cancelada")}
                  className="glass-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <X className="w-3.5 h-3.5" /> Cancelar solicitud
                </button>
              ) : (
                <button data-testid="profile-add-friend" disabled={busy}
                  onClick={() => call(() => userApi.post(`/social/friends/request/${profile.key}`), "Solicitud enviada (caduca en 7 días)")}
                  className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> Agregar amigo
                </button>
              )}
              <button data-testid="profile-follow" disabled={busy}
                onClick={() => call(() => userApi.post(`/social/follow/${profile.key}`))}
                className={`glass-btn ${relation.following ? "glass-btn-red" : ""} px-4 py-2 rounded-xl text-xs flex items-center gap-1.5`}>
                <Rss className="w-3.5 h-3.5" /> {relation.following ? "Siguiendo" : "Seguir"}
              </button>
              <button data-testid="profile-message" disabled={busy} onClick={message}
                className="glass-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> Mensaje
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
